// ✅ ALTERED ONLY FOR THEME (matches your “above theme”)
// - Orange header bar + white text
// - Page bg #F2F2F2 + inner white surfaces with soft blue border
// - Buttons: primary orange, outlined blue
// - Inputs/selects: soft blue border + orange focus
// - Table: blue header, soft zebra rows, hover tint, subtle borders
// - IconButtons: blue default, orange hover
// - No API/business logic changed

import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Button,
  IconButton,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import UploadIcon from "@mui/icons-material/Upload";

import { API_URL } from "./config";

const FIELDS = [
  "CampusCode",
  "EmployeeID",
  "FullName",
  "Gender",
  "DateOfBirth",
  "Nationality",
  "PhotoPath",
  "EmailAddress",
  "PhoneNumber",
  "AddressLine1",
  "City",
  "District",
  "State",
  "Zip",
  "EmergencyContactName",
  "EmergencyContactRelationship",
  "EmergencyContactPhone",
  "Department",
  "Designation",
  "EmployeeType",
  "DateOfJoining",
  "JobLocation",
  "SupervisorID",
  "Qualification",
  "Specialization",
  "TotalExperience",
  "RelevantExperience",
  "Certifications",
  "PreviousEmployers",
  "Achievements",
  "BankAccountNumber",
  "BankIFSC",
  "SalaryGrade",
  "PANNumber",
  "ContractType",
  "ContractStartDate",
  "ContractEndDate",
  "Username",
  "AccessRoles",
  "LastLogin",
  "AccessRights",
  "LeaveBalance",
  "MedicalInsurance",
  "ProvidentFundNumber",
  "HealthInfo",
  "MaritalStatus",
  "DependentInfo",
  "LanguageProficiency",
  "Hobbies",
  "AadhaarNumber",
  "VisaDetails",
  "BackgroundCheckStatus",
  "DisciplinaryActions",
  "IsActive",
];

const GENDER_OPTIONS = ["Male", "Female", "Non-Binary", "Prefer not to say"];
const EMP_TYPE_OPTIONS = ["Permanent", "Contractual", "Part-Time", "Visiting"];
const MARITAL_OPTIONS = ["Single", "Married", "Divorced", "Widowed"];
const BG_CHECK_OPTIONS = ["Cleared", "Pending"];

const EmployeeInfoManager = () => {
  // ✅ Theme constants (same family as your previous page)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };

  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const iconBtnSx = {
    borderRadius: 2,
    color: "rgba(25,118,210,0.95)",
    "&:hover": { backgroundColor: "rgba(240,120,40,0.10)", color: THEME_ORANGE },
  };

  const tableWrapSx = { ...surfaceSx, overflow: "hidden" };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
      py: 1,
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)", py: 0.8 },
    verticalAlign: "top",
  });

  // ===== Original state/logic =====
  const [data, setData] = useState([]);
  const [newRow, setNewRow] = useState(() => {
    const base = {};
    FIELDS.forEach((f) => (base[f] = ""));
    return base;
  });

  // ✅ Select handler (works for add + edit)
  const handleSelectChange = (field, id = null) => (e) => {
    const value = e.target.value;
    if (id == null) {
      setNewRow((r) => ({ ...r, [field]: value }));
    } else {
      setData((d) => d.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
    }
  };

  const [editRowId, setEditRowId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const [loginCampusCode, setCampusCode] = useState("");
  const [searchEmployeeId, setSearchEmployeeId] = useState("");

  const downloadSampleExcel = () => {
    const sampleRows = [
      {
        CampusCode: loginCampusCode,
        EmployeeID: "EMP001",
        FullName: "xxx xxx",
        Gender: "Female",
        DateOfBirth: "1992-06-15",
        Nationality: "Indian",
        PhotoPath: "",
        EmailAddress: "xxx@example.com",
        PhoneNumber: "9876543210",
        AddressLine1: "1-2-3, Main Road",
        City: "Kakinada",
        District: "Kakinada",
        State: "Andhra Pradesh",
        Zip: "533001",
        EmergencyContactName: "xxx",
        EmergencyContactRelationship: "Spouse",
        EmergencyContactPhone: "9876543210",
        Department: "CSE",
        Designation: "Assistant Professor",
        EmployeeType: "Permanent",
        DateOfJoining: "2024-06-01",
        JobLocation: "Main Campus",
        SupervisorID: "",
        Qualification: "M.Tech",
        Specialization: "AI",
        TotalExperience: 5,
        RelevantExperience: 3,
        Certifications: "",
        PreviousEmployers: "",
        Achievements: "",
        BankAccountNumber: "",
        BankIFSC: "",
        SalaryGrade: "",
        PANNumber: "",
        ContractType: "",
        ContractStartDate: "",
        ContractEndDate: "",
        Username: "aishwarya",
        AccessRoles: "Teacher",
        LastLogin: "",
        AccessRights: "",
        LeaveBalance: 0,
        MedicalInsurance: "",
        ProvidentFundNumber: "",
        HealthInfo: "",
        MaritalStatus: "Married",
        DependentInfo: "",
        LanguageProficiency: "",
        Hobbies: "",
        AadhaarNumber: "",
        VisaDetails: "",
        BackgroundCheckStatus: "Cleared",
        DisciplinaryActions: "",
        IsActive: "Active",
      },
      {
        CampusCode: loginCampusCode || "KIET01",
        EmployeeID: "EMP002",
        FullName: "xxx xxx",
        Gender: "Male",
        DateOfBirth: "1988-01-10",
        Nationality: "Indian",
        PhotoPath: "",
        EmailAddress: "xxx@example.com",
        PhoneNumber: "9123456780",
        AddressLine1: "Near Bus Stand",
        City: "Rajahmundry",
        District: "East Godavari",
        State: "Andhra Pradesh",
        Zip: "533101",
        EmergencyContactName: "xxx",
        EmergencyContactRelationship: "Brother",
        EmergencyContactPhone: "9000000000",
        Department: "Admin",
        Designation: "Accountant",
        EmployeeType: "Contractual",
        DateOfJoining: "2023-07-01",
        JobLocation: "Main Campus",
        SupervisorID: "",
        Qualification: "B.Com",
        Specialization: "",
        TotalExperience: 8,
        RelevantExperience: 8,
        Certifications: "",
        PreviousEmployers: "",
        Achievements: "",
        BankAccountNumber: "",
        BankIFSC: "",
        SalaryGrade: "",
        PANNumber: "",
        ContractType: "Annual",
        ContractStartDate: "2023-07-01",
        ContractEndDate: "2024-06-30",
        Username: "suresh",
        AccessRoles: "Staff",
        LastLogin: "",
        AccessRights: "",
        LeaveBalance: 2,
        MedicalInsurance: "",
        ProvidentFundNumber: "",
        HealthInfo: "",
        MaritalStatus: "Single",
        DependentInfo: "",
        LanguageProficiency: "",
        Hobbies: "",
        AadhaarNumber: "",
        VisaDetails: "",
        BackgroundCheckStatus: "Pending",
        DisciplinaryActions: "",
        IsActive: "Active",
      },
    ];

    const normalizedRows = sampleRows.map((r) => {
      const obj = {};
      FIELDS.forEach((f) => {
        obj[f] = r[f] ?? "";
      });
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(normalizedRows, { header: FIELDS });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Employee");
    ws["!cols"] = FIELDS.map((h) => ({ wch: Math.max(12, h.length + 2) }));

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, `Employee_BulkUpload_Sample_${loginCampusCode || "Campus"}.xlsx`);
  };

  const fileInputRef = useRef();
  const token = localStorage.getItem("auth");

  const convertToISTDate = (utcTime) => {
    if (!utcTime) return "";
    const d = new Date(utcTime);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  };

  const getIsActiveValue = (val) => {
    if (val === 1 || val === "1" || val === true) return "Active";
    if (val === 0 || val === "0" || val === false) return "InActive";
    if (val === "Active" || val === "InActive") return val;
    return "";
  };

  useEffect(() => {
    if (!token) return;
    try {
      const decoded = JSON.parse(atob(token.split(".")[1]));
      const cc = decoded.campusCode || decoded.CampusCode || "";
      setCampusCode(cc);
      setNewRow((r) => ({ ...r, CampusCode: cc }));
    } catch {
      setError("Failed to decode authentication token");
    }
  }, [token]);

  const fetchEmployees = async (empId = "") => {
    try {
      const res = await axios.get(`${API_URL}/api/Employee`, {
        params: { EmployeeIDs: empId.trim() },
        headers: { Authorization: `Bearer ${token}` },
      });

      const rows = (res.data || []).map((r) => ({
        ...r,
        id: r.id ?? r.ID, // ✅ normalize
      }));

      setData(rows);
    } catch {
      setError("Failed to fetch employee data");
    }
  };

  useEffect(() => {
    if (loginCampusCode) fetchEmployees();
  }, [loginCampusCode]);

  const handleInputChange = (e, id = null) => {
    const { name, value } = e.target;
    if (id == null) setNewRow((r) => ({ ...r, [name]: value }));
    else setData((d) => d.map((row) => (row.id === id ? { ...row, [name]: value } : row)));
  };

  const handleAddRow = async () => {
    try {
      const payload = { ...newRow, CampusCode: loginCampusCode };
      const { data: created } = await axios.post(`${API_URL}/api/Employee`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const createdRow = { ...created, id: created.id ?? created.ID };
      setData((d) => [...d, createdRow]);

      setNewRow(() => {
        const base = {};
        FIELDS.forEach((f) => (base[f] = ""));
        base.CampusCode = loginCampusCode;
        return base;
      });

      setSuccess(true);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to add employee info");
    }
  };

  const handleUpdateRow = async (id) => {
    const updated = data.find((r) => r.id === id);
    if (!updated) return;
    try {
      await axios.put(`${API_URL}/api/Employee/${id}`, updated, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEditRowId(null);
      setSuccess(true);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update employee info");
    }
  };

  const handleDeleteRow = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/Employee/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData((d) => d.filter((r) => r.id !== id));
      setSuccess(true);
      setError("");
    } catch {
      setError("Failed to delete record");
    }
  };

  const handleBulkClick = () => fileInputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const form = new FormData();
    form.append("excelFile", file);
    form.append("CampusCode", loginCampusCode);

    try {
      await axios.post(`${API_URL}/api/UploadEmployeeInformation`, form, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      await fetchEmployees(searchEmployeeId);
      setSuccess(true);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Bulk upload for employee info failed");
    } finally {
      e.target.value = "";
    }
  };

  const handleSearch = async () => {
    await fetchEmployees(searchEmployeeId);
  };

  const isDateField = (f) => ["DateOfBirth", "DateOfJoining", "ContractStartDate", "ContractEndDate"].includes(f);

  const isEnumField = (f) => ["Gender", "EmployeeType", "MaritalStatus", "BackgroundCheckStatus"].includes(f);

  const renderSelectOptions = (field) => {
    let opts = [];
    if (field === "Gender") opts = GENDER_OPTIONS;
    if (field === "EmployeeType") opts = EMP_TYPE_OPTIONS;
    if (field === "MaritalStatus") opts = MARITAL_OPTIONS;
    if (field === "BackgroundCheckStatus") opts = BG_CHECK_OPTIONS;

    return [
      <MenuItem key="__empty" value="">
        <em>Select</em>
      </MenuItem>,
      ...opts.map((o) => (
        <MenuItem key={o} value={o}>
          {o}
        </MenuItem>
      )),
    ];
  };

  return (
    <Box sx={{ p: 2, backgroundColor: BG }}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Upload Employee Information
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Search by Employee ID, edit inline, or bulk upload via Excel
            </Typography>
          </Box>
        </Box>

        <Box sx={bodySx}>
          {/* Alerts */}
          {!!error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
                mb: 2,
              }}
            >
              {error}
            </Alert>
          )}

          {!!success && (
            <Snackbar open autoHideDuration={3000} onClose={() => setSuccess(false)}>
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                Operation successful!
              </Alert>
            </Snackbar>
          )}

          {/* Search + Actions */}
          <Paper variant="outlined" sx={{ ...surfaceSx, p: 2, mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                <TextField
                  label="Enter Employee ID"
                  value={searchEmployeeId}
                  onChange={(e) => setSearchEmployeeId(e.target.value)}
                  size="small"
                  sx={{ minWidth: 240, ...inputSx }}
                />
                <Button variant="contained" sx={primaryBtnSx} onClick={handleSearch}>
                  Search
                </Button>
              </Box>

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button variant="outlined" sx={outlinedBtnSx} onClick={downloadSampleExcel}>
                  Download Sample
                </Button>

                <Button variant="contained" sx={primaryBtnSx} startIcon={<UploadIcon />} onClick={handleBulkClick}>
                  Bulk Upload
                </Button>

                <input
                  type="file"
                  accept=".xlsx,.xls"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFile}
                />
              </Box>
            </Box>

            <Divider sx={{ mt: 2, borderColor: "rgba(25,118,210,0.12)" }} />
            <Typography sx={{ mt: 1, fontSize: "0.85rem", opacity: 0.75 }}>
              Tip: Edit a row and click Save. Use bulk upload for large imports.
            </Typography>
          </Paper>

          {/* Table */}
      {/* OUTER – keeps rounded corners & spacing */}
      <Paper elevation={0} sx={tableWrapSx}>

        {/* INNER – ONLY for horizontal scroll */}
        <Box
          sx={{
            overflowX: "auto",
            overflowY: "hidden",
          }}
        >
          <TableContainer component="div">
            <Table size="small" sx={{ width: "100%" }}>
              <TableHead>
                <TableRow sx={theadRowSx}>
                  <TableCell>#</TableCell>
                  {FIELDS.map((f) => (
                    <TableCell key={f}>{f}</TableCell>
                  ))}
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {data.map((row, idx) =>
                  editRowId === row.id ? (
                    <TableRow key={row.id} sx={zebraRowSx(idx)}>
                      <TableCell>{idx + 1}</TableCell>

                      {FIELDS.map((field) => (
                        <TableCell key={field}>
                          {field === "CampusCode" ? (
                            <TextField value={row[field]} disabled size="small" fullWidth sx={inputSx} />
                          ) : field === "IsActive" ? (
                            <Select
                              value={getIsActiveValue(row.IsActive)}
                              onChange={handleSelectChange("IsActive", row.id)}
                              size="small"
                              fullWidth
                              displayEmpty
                              sx={inputSx}
                            >
                              <MenuItem value="">
                                <em>Select</em>
                              </MenuItem>
                              <MenuItem value="Active">Active</MenuItem>
                              <MenuItem value="InActive">InActive</MenuItem>
                            </Select>
                          ) : isEnumField(field) ? (
                            <TextField
                              select
                              value={row[field] ?? ""}
                              onChange={handleSelectChange(field, row.id)}
                              size="small"
                              fullWidth
                              sx={inputSx}
                            >
                              {renderSelectOptions(field)}
                            </TextField>
                          ) : isDateField(field) ? (
                            <TextField
                              name={field}
                              type="date"
                              value={convertToISTDate(row[field])}
                              onChange={(e) => handleInputChange(e, row.id)}
                              size="small"
                              fullWidth
                              sx={inputSx}
                            />
                          ) : (
                            <TextField
                              name={field}
                              value={row[field] ?? ""}
                              onChange={(e) => handleInputChange(e, row.id)}
                              size="small"
                              fullWidth
                              sx={inputSx}
                            />
                          )}
                        </TableCell>
                      ))}

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <IconButton sx={iconBtnSx} onClick={() => handleUpdateRow(row.id)} title="Save">
                          <SaveIcon fontSize="small" />
                        </IconButton>
                        <IconButton sx={iconBtnSx} onClick={() => setEditRowId(null)} title="Cancel">
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow key={row.id} sx={zebraRowSx(idx)}>
                      <TableCell>{idx + 1}</TableCell>
                      {FIELDS.map((field) => (
                        <TableCell key={field}>
                          {isDateField(field)
                            ? convertToISTDate(row[field])
                            : field === "LastLogin"
                            ? row[field]
                              ? new Date(row[field]).toLocaleString("en-IN")
                              : ""
                            : field === "IsActive"
                            ? getIsActiveValue(row.IsActive)
                            : row[field]}
                        </TableCell>
                      ))}
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <IconButton sx={iconBtnSx} onClick={() => setEditRowId(row.id)} title="Edit">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton sx={iconBtnSx} onClick={() => handleDeleteRow(row.id)} title="Delete">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                )}

                {/* new row */}
                <TableRow sx={zebraRowSx(data.length)}>
                  <TableCell />
                  {FIELDS.map((field) => (
                    <TableCell key={field}>
                      {field === "CampusCode" ? (
                        <TextField value={loginCampusCode} disabled size="small" fullWidth sx={inputSx} />
                      ) : field === "IsActive" ? (
                        <Select
                          value={newRow.IsActive ?? ""}
                          onChange={handleSelectChange("IsActive")}
                          size="small"
                          fullWidth
                          displayEmpty
                          sx={inputSx}
                        >
                          <MenuItem value="">
                            <em>Select</em>
                          </MenuItem>
                          <MenuItem value="Active">Active</MenuItem>
                          <MenuItem value="InActive">InActive</MenuItem>
                        </Select>
                      ) : isEnumField(field) ? (
                        <TextField
                          select
                          value={newRow[field] ?? ""}
                          onChange={handleSelectChange(field)}
                          size="small"
                          fullWidth
                          sx={inputSx}
                        >
                          {renderSelectOptions(field)}
                        </TextField>
                      ) : isDateField(field) ? (
                        <TextField
                          name={field}
                          type="date"
                          value={newRow[field] ?? ""}
                          onChange={handleInputChange}
                          size="small"
                          fullWidth
                          sx={inputSx}
                        />
                      ) : (
                        <TextField
                          name={field}
                          value={newRow[field] ?? ""}
                          onChange={handleInputChange}
                          size="small"
                          fullWidth
                          sx={inputSx}
                        />
                      )}
                    </TableCell>
                  ))}
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <IconButton sx={iconBtnSx} onClick={handleAddRow} title="Add">
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>

                {!data?.length && (
                  <TableRow>
                    <TableCell colSpan={FIELDS.length + 2} sx={{ py: 3, textAlign: "center", opacity: 0.7 }}>
                      No employee records found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
};

export default EmployeeInfoManager;
